import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { getMediaUrl } from '../services/api';
import { FaFacebook, FaTwitter, FaLinkedinIn, FaEnvelope, FaClock, FaCalendarAlt, FaUser, FaDownload, FaArrowLeft } from 'react-icons/fa';
import './PageView.css';

const PageView = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableOfContents, setTableOfContents] = useState([]);
  const [relatedDocs, setRelatedDocs] = useState([]);

  useEffect(() => {
    loadPage();
  }, [slug]);

  const loadPage = async () => {
    try {
      const response = await api.get(`/pages/${slug}/`);
      setPage(response.data);
      
      // Generate table of contents if enabled
      if (response.data.show_table_of_contents) {
        generateTableOfContents(response.data.content);
      }
      
      // Parse related documents if available
      if (response.data.related_documents) {
        try {
          setRelatedDocs(JSON.parse(response.data.related_documents));
        } catch (e) {
          console.log('Could not parse related documents');
        }
      }
    } catch (error) {
      console.error('Error loading page:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateTableOfContents = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = doc.querySelectorAll('h2, h3, h4');
    const toc = Array.from(headings).map((heading, index) => ({
      id: `heading-${index}`,
      text: heading.textContent,
      level: parseInt(heading.tagName[1]),
      tagName: heading.tagName
    }));
    setTableOfContents(toc);
  };

  const addIdsToContent = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = doc.querySelectorAll('h2, h3, h4');
    headings.forEach((heading, index) => {
      heading.id = `heading-${index}`;
    });
    return doc.body.innerHTML;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const text = `Check out this article: ${page.title}`;
    let shareUrl = '';
    
    switch(platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank');
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  if (!page) {
    return (
      <Container className="py-5">
        <h1>Page Not Found</h1>
        <Link to="/" className="btn btn-primary">Go Home</Link>
      </Container>
    );
  }

  const pageStyle = {
    backgroundColor: page.background_color || 'transparent',
    color: page.text_color || 'inherit',
  };

  const renderLayout = () => {
    switch (page.layout_type) {
      case 'sidebar':
        return renderLayoutWithSidebar();
      case 'two-column':
        return renderTwoColumnLayout();
      case 'minimal':
        return renderMinimalLayout();
      case 'with_sections':
        return renderWithSectionsLayout();
      case 'default':
      default:
        return renderDefaultLayout();
    }
  };

  const renderDefaultLayout = () => (
    <article className="page-article page-layout-default">
      {page.show_breadcrumb && <Breadcrumb />}
      
      {page.featured_image && (
        <div className="page-featured-image-container mb-5">
          <img 
            src={getMediaUrl(page.featured_image)} 
            alt={page.title}
            className="page-featured-image img-fluid"
          />
        </div>
      )}
      
      <div className="page-header mb-4">
        <h1 className="page-title">{page.title}</h1>
        <PageMetadata page={page} formatDate={formatDate} />
      </div>

      <div 
        className="page-content"
        dangerouslySetInnerHTML={{ __html: addIdsToContent(page.content) }}
      />

      {page.show_author_info && page.author_name && <AuthorCard page={page} />}
      {page.show_share_buttons && <SocialShareButtons onShare={shareOnSocial} />}
    </article>
  );

  const renderLayoutWithSidebar = () => (
    <Row className="page-layout-sidebar">
      <Col lg={8}>
        {page.show_breadcrumb && <Breadcrumb />}
        
        {page.featured_image && (
          <div className="page-featured-image-container mb-5">
            <img 
              src={getMediaUrl(page.featured_image)} 
              alt={page.title}
              className="page-featured-image img-fluid"
            />
          </div>
        )}
        
        <div className="page-header mb-4">
          <h1 className="page-title">{page.title}</h1>
          <PageMetadata page={page} formatDate={formatDate} />
        </div>

        <div 
          className="page-content"
          dangerouslySetInnerHTML={{ __html: addIdsToContent(page.content) }}
        />

        {page.show_author_info && page.author_name && <AuthorCard page={page} />}
        {page.show_share_buttons && <SocialShareButtons onShare={shareOnSocial} />}
      </Col>

      <Col lg={4}>
        <aside className="page-sidebar">
          {tableOfContents.length > 0 && page.show_table_of_contents && (
            <Card className="sidebar-widget table-of-contents mb-4">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">Table of Contents</h5>
              </Card.Header>
              <Card.Body>
                <ul>
                  {tableOfContents.map(item => (
                    <li key={item.id} style={{ marginLeft: `${(item.level - 2) * 15}px` }}>
                      <a href={`#${item.id}`}>{item.text}</a>
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          )}

          {relatedDocs.length > 0 && (
            <Card className="sidebar-widget related-documents mb-4">
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0"><FaDownload className="me-2" />Related Documents</h5>
              </Card.Header>
              <Card.Body>
                <ul>
                  {relatedDocs.map((doc, idx) => (
                    <li key={idx}>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer">
                        {doc.name} <FaDownload className="ms-1" style={{fontSize: '0.75rem'}} />
                      </a>
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          )}

          {page.sidebar_widgets && (
            <Card className="sidebar-widget custom-widgets mb-4">
              <Card.Body dangerouslySetInnerHTML={{ __html: page.sidebar_widgets }} />
            </Card>
          )}
        </aside>
      </Col>
    </Row>
  );

  const renderTwoColumnLayout = () => (
    <Row className="page-layout-two-column">
      <Col md={6} className="mb-4 mb-md-0">
        {page.featured_image && (
          <div className="page-featured-image-container">
            <img 
              src={getMediaUrl(page.featured_image)} 
              alt={page.title}
              className="page-featured-image img-fluid w-100"
            />
          </div>
        )}
      </Col>
      <Col md={6}>
        <div className="page-header mb-4">
          <h1 className="page-title">{page.title}</h1>
          <PageMetadata page={page} formatDate={formatDate} />
        </div>
        <div 
          className="page-content"
          dangerouslySetInnerHTML={{ __html: addIdsToContent(page.content) }}
        />
      </Col>
    </Row>
  );

  const renderMinimalLayout = () => (
    <article className="page-layout-minimal max-width-800">
      <div className="page-header text-center mb-5">
        <h1 className="page-title">{page.title}</h1>
        <PageMetadata page={page} formatDate={formatDate} />
      </div>

      {page.featured_image && (
        <div className="page-featured-image-container mb-4">
          <img 
            src={getMediaUrl(page.featured_image)} 
            alt={page.title}
            className="page-featured-image img-fluid"
          />
        </div>
      )}

      <div 
        className="page-content"
        dangerouslySetInnerHTML={{ __html: addIdsToContent(page.content) }}
      />
    </article>
  );

  const renderWithSectionsLayout = () => (
    <div className="page-layout-sections">
      {page.show_breadcrumb && <Breadcrumb />}
      
      <section className="page-header-section" style={pageStyle}>
        <Container>
          <h1 className="page-title">{page.title}</h1>
          {page.featured_image && (
            <div className="page-featured-image-container">
              <img 
                src={getMediaUrl(page.featured_image)} 
                alt={page.title}
                className="page-featured-image"
              />
            </div>
          )}
        </Container>
      </section>

      <section className="page-content-section">
        <Container>
          <div 
            className="page-content"
            dangerouslySetInnerHTML={{ __html: addIdsToContent(page.content) }}
          />
          {page.show_author_info && page.author_name && <AuthorCard page={page} />}
        </Container>
      </section>
    </div>
  );

  const Breadcrumb = () => (
    <nav className="page-breadcrumb mb-4">
      <Link to="/" className="breadcrumb-link">Home</Link>
      <span className="breadcrumb-separator"> / </span>
      <span className="breadcrumb-current">{page.title}</span>
    </nav>
  );

  const PageMetadata = ({ page, formatDate }) => (
    <div className="page-metadata">
      {page.show_publish_date && page.publish_date && (
        <span className="metadata-item">
          <FaCalendarAlt className="me-2" />
          Published on {formatDate(page.publish_date)}
        </span>
      )}
      {page.reading_time && (
        <span className="metadata-item">
          <FaClock className="me-2" />
          {page.reading_time} min read
        </span>
      )}
      {page.show_author_info && page.author_name && (
        <span className="metadata-item">
          <FaUser className="me-2" />
          By <strong>{page.author_name}</strong>
        </span>
      )}
    </div>
  );

  const AuthorCard = ({ page }) => (
    <Card className="author-card mt-5 mb-4">
      <Card.Body>
        <h5 className="card-title mb-3">
          <FaUser className="me-2" />
          About the Author
        </h5>
        <div className="author-info">
          <h6 className="author-name">{page.author_name}</h6>
          {page.author_email && (
            <p className="author-email mb-2">
              <strong>Email:</strong> <a href={`mailto:${page.author_email}`}>{page.author_email}</a>
            </p>
          )}
          {page.author_bio && (
            <p className="author-bio">{page.author_bio}</p>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  const SocialShareButtons = ({ onShare }) => (
    <Card className="share-buttons-card mt-4 mb-4">
      <Card.Body>
        <h6 className="mb-3">Share This Page</h6>
        <div className="share-buttons">
          <button className="btn btn-social btn-facebook me-2" onClick={() => onShare('facebook')} title="Share on Facebook">
            <FaFacebook /> Facebook
          </button>
          <button className="btn btn-social btn-twitter me-2" onClick={() => onShare('twitter')} title="Share on Twitter">
            <FaTwitter /> Twitter
          </button>
          <button className="btn btn-social btn-linkedin me-2" onClick={() => onShare('linkedin')} title="Share on LinkedIn">
            <FaLinkedinIn /> LinkedIn
          </button>
          <button className="btn btn-social btn-email" onClick={() => window.location.href = `mailto:?subject=${page.title}&body=${window.location.href}`} title="Share via Email">
            <FaEnvelope /> Email
          </button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container className="page-view py-5" style={pageStyle}>
      <div className="page-back-link mb-4">
        <Link to="/" className="btn btn-link btn-sm text-decoration-none">
          <FaArrowLeft className="me-2" />
          Back to Home
        </Link>
      </div>
      {renderLayout()}
    </Container>
  );
};

export default PageView;
